import { useUserGetUserHistoryInformation } from "@crowdcoursing/api/openapi";
import { FilterAlt, FilterAltOff } from "@mui/icons-material";
import { Avatar, Box, Chip, IconButton, Link as MuiLink, Typography, useMediaQuery, useTheme } from "@mui/material";
import { GridColumnMenu, type GridFilterOperator, useGridApiRef } from "@mui/x-data-grid-pro";
import { useCallback, useMemo, useState } from "react";
import { FormSpy } from "react-final-form";
import { type PartialDeep } from "type-fest";

import { generateMeetingTime } from "~utils/date";

import { RightSidebar } from "../../../../../src/cc-ui";
import { Route } from "../../../../services/router/Route";
import { PhoneAndEmailRenderer } from "../../../atoms/DataGrid";
import { DateRange, type DateRangeValue, FIELD_NAME_DATE_RANGE, Form } from "../../../atoms/Form";
import { RouteLink } from "../../../atoms/RouteLink";
import { DataGrid, type DataGridProps } from "../../../molecules/DataGrid/DataGrid";
import { UserHistory } from "../../../molecules/UserHistory/UserHistory";

const tAttendeeName = "Attendee";
const tOwnerName = "Leader Name";
const tContact = "Contact";
const tEvent = "Event";
const tRoom = "Room";
const tEventTime = "Event Time";
const tStatus = "Status";

export interface SchoolRsvp {
  attendeeUserId: string;
  contactInformation: { email: string; phone: string };
  event: { id: string; name: string };
  attendeeName: string;
  meetingTime: { beginMeetingTime: Date; endMeetingTime: Date };
  room: { id: string; name: string };
  rsvpId: string;
  isVetted: boolean;
  ownerFirstName: string;
  ownerLastName: string;
}

export type DataGridSchoolRsvp = PartialDeep<SchoolRsvp>;

type RowDataBase = PartialDeep<{
  volunteerStatus: string;
  ownerName: string;
  attendeeName: string;
}>;

type RowData = Omit<DataGridSchoolRsvp, "ownerLastName" | "ownerFirstName" | "isVetted"> &
  RowDataBase & {
    onNameClick: (name: string) => void;
    /** id referenced in data grid operations. MUST BE UNIQUE */
    rowId: string;
  };

const generateRows = (
  { attendeeName, isVetted, ownerFirstName, ownerLastName, ...rest }: DataGridSchoolRsvp,
  onNameClick: (name: string) => void,
): RowData => {
  return {
    attendeeName: attendeeName,
    onNameClick,
    ownerName: `${ownerLastName}, ${ownerFirstName}`,
    rowId: window.crypto.randomUUID(),
    volunteerStatus: isVetted ? "Vetted" : "Unvetted",
    ...rest,
  };
};

const columns: DataGridProps<RowData>["columns"] = [
  {
    field: "attendeeName",
    filterable: false,
    headerName: tAttendeeName,
    renderCell: ({ row }) => {
      return (
        <Box
          onClick={() => row.onNameClick(row.attendeeName ?? "")}
          sx={{
            "&:hover": {
              color: (theme) => theme.palette.primary.dark,
              textDecoration: "underline",
            },
            cursor: "pointer",
          }}
        >
          {row.attendeeName}
        </Box>
      );
    },
    width: 128,
  },
  { field: "volunteerStatus", filterable: false, headerName: tStatus, width: 160 },
  {
    field: "contactInformation",
    filterable: false,
    headerName: tContact,
    renderCell: ({ row: { contactInformation } }) =>
      contactInformation && <PhoneAndEmailRenderer {...contactInformation} />,
    valueGetter: ({ row: { contactInformation } }) =>
      contactInformation && `${contactInformation.email || ""} ${contactInformation.phone || ""}`.trim(),
    width: 256,
  },
  {
    field: "room",
    filterable: false,
    headerName: tRoom,
    renderCell: ({ row: { room } }) => {
      if (!room) {
        return null;
      }

      const { id, name } = room;

      if (!name) {
        return null;
      }

      if (!id) {
        return <Typography>{name}</Typography>;
      }

      return <RouteLink to={Route.roomView({ roomId: id })}>{name}</RouteLink>;
    },
    valueGetter: ({ row: { room } }) => room && room.name?.trim(),
    width: 192,
  },
  {
    field: "event",
    filterable: false,
    headerName: tEvent,
    renderCell: ({ row: { event } }) => {
      if (!event) {
        return null;
      }

      const { id, name } = event;

      if (!name) {
        return null;
      }

      if (!id) {
        return <Typography>{name}</Typography>;
      }

      return <RouteLink to={Route.eventView({ eventId: id })}>{name}</RouteLink>;
    },
    valueGetter: ({ row: { event } }) => event && event.name?.trim(),
    width: 192,
  },
  {
    field: "meetingTime",
    filterOperators: [
      {
        getApplyFilterFn: () => null,
        getApplyFilterFnV7: (filterItem) => {
          if (!Array.isArray(filterItem.value) || filterItem.value.length !== 2) {
            return null;
          }

          if (filterItem.value.some((value) => !value)) {
            return null;
          }

          // (value, row, column, apiRef) => null | boolean
          return (_, row) => {
            const { beginMeetingTime, endMeetingTime } = row.meetingTime;
            const [filterBegin, filterEnd]: DateRangeValue = filterItem.value;

            if (!filterBegin) {
              console.error("(SchoolRsvps)(columns)(getApplyFilterFnV7): filterBegin is null or undefined");
              return false;
            }

            if (!filterEnd) {
              console.error("(SchoolRsvps)(columns)(getApplyFilterFnV7): filterEnd is null or undefined");
              return false;
            }

            return (
              beginMeetingTime >= filterBegin.startOf("day").toJSDate() &&
              endMeetingTime <= filterEnd.endOf("day").toJSDate()
            );
          };
        },
        label: "Between",
        value: "between",
      } satisfies GridFilterOperator,
    ],
    filterable: true,
    headerName: tEventTime,
    renderCell: ({ row: { meetingTime } }) => {
      if (!meetingTime) {
        return null;
      }

      const { beginMeetingTime, endMeetingTime } = meetingTime;

      if (!beginMeetingTime || !endMeetingTime) {
        return null;
      }

      return generateMeetingTime(beginMeetingTime, endMeetingTime);
    },
    valueGetter: ({ row: { meetingTime } }) => {
      if (!meetingTime) {
        return null;
      }

      const { beginMeetingTime, endMeetingTime } = meetingTime;

      if (!beginMeetingTime || !endMeetingTime) {
        return null;
      }

      return generateMeetingTime(beginMeetingTime, endMeetingTime);
    },
    width: 192,
  },
  { field: "ownerName", filterable: false, headerName: tOwnerName, width: 192 },
];

interface FormValues {
  [FIELD_NAME_DATE_RANGE]: DateRangeValue;
}

export type SchoolRsvpsProps<Data = DataGridSchoolRsvp[]> = Utils.WithQueryInfo<Data>;

export const SchoolRsvps = ({ data, refetch, status }: SchoolRsvpsProps) => {
  const apiRef = useGridApiRef();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [selectedUserName, setSelectedUserName] = useState<string | null>(null);
  const selectedUser = useMemo(
    () => data?.find((d) => `${d.attendeeName}` === selectedUserName),
    [data, selectedUserName],
  );
  const { data: userHistoryData } = useUserGetUserHistoryInformation(
    { pathParams: { userId: selectedUser?.attendeeUserId ?? "" } },
    { enabled: !!selectedUser?.attendeeUserId },
  );

  const onNameClick = useCallback((name: string) => {
    setSelectedUserName(name);
  }, []);

  const rows = useMemo(() => data?.map((d) => generateRows(d, onNameClick)) ?? [], [data, onNameClick]);

  const setFilter = useCallback(
    (value: FormValues["dateRange"]) => {
      if (apiRef.current) {
        apiRef.current.setFilterModel({
          items: [{ field: "meetingTime", operator: "between", value }],
        });
      }
    },
    [apiRef],
  );

  const clearFilter = useCallback(() => {
    if (apiRef.current) {
      apiRef.current.setFilterModel({ items: [] });
      apiRef.current.setPage(0);
    }
  }, [apiRef]);

  return (
    <>
      <RightSidebar
        headingText="User Details"
        label={() => null}
        onClose={() => setSelectedUserName(null)}
        open={!!selectedUserName}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "2rem",
            pb: 4,
          }}
        >
          <Box sx={{ alignItems: "center", display: "flex", gap: "1rem" }}>
            <Avatar
              alt={selectedUserName ?? undefined}
              src={userHistoryData?.profileImageUri ?? undefined}
              sx={{ height: 72, width: 72 }}
            >
              {selectedUserName?.[0]}
            </Avatar>
            <Box>
              <Typography sx={{ mb: 0.25 }} variant="h6">
                {selectedUserName}
              </Typography>
              {selectedUser?.isVetted !== undefined && (
                <Box sx={{ mb: 0.25 }}>
                  <Chip label={selectedUser.isVetted ? "Vetted" : "Unvetted"} size="small" />
                </Box>
              )}
              <Typography color="text.secondary" sx={{ mb: 0.25 }} variant="body2">
                {selectedUser?.contactInformation?.email ? (
                  <MuiLink
                    href={`mailto:${selectedUser.contactInformation.email}`}
                    sx={{
                      "&:hover": {
                        textDecoration: "underline",
                      },
                      color: "primary.main",
                      textDecoration: "none",
                    }}
                  >
                    {selectedUser.contactInformation.email}
                  </MuiLink>
                ) : (
                  "No email provided"
                )}
              </Typography>
              <Typography color="text.secondary" variant="body2">
                {selectedUser?.contactInformation?.phone}
              </Typography>
            </Box>
          </Box>
          <UserHistory data={userHistoryData} showUserUpcomingRsvps showVolunteerStats status="success" />
        </Box>
      </RightSidebar>
      <Box
        sx={{
          display: "grid",
          maxWidth: "100%",
          minWidth: 0,
          overflowX: "hidden",
          overflowY: "auto",
        }}
      >
        <DataGrid<RowData>
          apiRef={apiRef}
          columns={columns}
          disableColumnFilter={false}
          headerWidgets={
            !isMobile ? (
              <Form<FormValues>
                onSubmit={(values) => setFilter(values[FIELD_NAME_DATE_RANGE])}
                slotProps={{
                  renderContainer: {
                    alignItems: "center",
                    display: "grid",
                    gap: "2rem",
                    gridAutoColumns: "minmax(0, 208px) minmax(0, auto)",
                    gridAutoFlow: "column",
                  },
                }}
              >
                <DateRange />
                <FormSpy<FormValues>
                  render={({ form, values }) => {
                    const disabled = !(
                      values[FIELD_NAME_DATE_RANGE]?.length == 2 &&
                      values[FIELD_NAME_DATE_RANGE]?.every((value) => !!value)
                    );

                    return (
                      <Box>
                        <IconButton
                          aria-label="Filter rsvps"
                          color="primary"
                          disabled={disabled}
                          onClick={() => form.submit()}
                        >
                          <FilterAlt />
                        </IconButton>
                        <IconButton
                          aria-label="Clear filter"
                          color="primary"
                          onClick={() => {
                            form.initialize({ dateRange: undefined });
                            clearFilter();
                          }}
                        >
                          <FilterAltOff />
                        </IconButton>
                      </Box>
                    );
                  }}
                />
              </Form>
            ) : null
          }
          rows={rows}
          slotProps={{
            error: { slotProps: { button: { onClick: refetch } } },
          }}
          slots={{
            ...(!isMobile ? {} : { toolbar: null }),
            columnHeaderFilterIconButton: () => null,
            columnMenu: (props) => {
              return (
                <GridColumnMenu
                  {...props}
                  slotProps={{
                    ...props?.slotProps,
                    columnMenuFilterItem: { ...props?.slotProps?.columnMenuFilterItem, displayOrder: 0 },
                  }}
                />
              );
            },
          }}
          status={status}
        />
      </Box>
    </>
  );
};
